<template>
  <v-skeleton-loader v-if="loading" type="img"/>
  <div v-else-if="loaded">
    <div v-show="!isEmpty(vehicleStates)">
      <gmap-map class="vehicle-gps-map" ref="map" :zoom="12" :center="dummyCenter"/>
      <v-subheader>
        {{ $t('sensor_data_from_past_24_hours_only') }}
      </v-subheader>
    </div>
    <div v-if="isEmpty(vehicleStates)">
      <span class="text-subtitle-1">
        {{ $t('24_hr_no_vehicle_sensors') }}
      </span>
    </div>
  </div>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import Moment from 'moment-timezone'
import { each, isEmpty } from 'lodash'
import { gmapApi } from 'vue2-google-maps'

// ESlint no-undef is turned off because google (defined) in imports elsewhere is used throughout.
/* eslint no-undef: "off" */
export default {
  mixins: [
    CurrentUserMixin
  ],
  // This component displays a vehicle's recent GPS locations on a google map.
  name: 'VehicleGpsMap',
  props: {
    vehicle: Object
  },
  data () {
    return {
      loading: false,
      loaded: false,
      vehicleStates: [],
      // local reference to google map, will be set when map promise resolves.
      map: undefined,
      // vue-google-maps complains if no center is defined,
      // however we can't calculate the bounds until the map promise resolves and window.google is available.
      dummyCenter: { lat: 0, lng: 0 },
      // A reference to the Google Maps PolyLine that shows the vehicle's path.
      vehiclePath: null,
      // A reference to the collected gps markers
      gpsMarkers: []
    }
  },
  computed: {
    /**
     * Expose imported gmapApi symbol as 'google' so we can use 'google.maps' object inside Vue as we would normally in
     * any other Javascript project.
     * (As recommended by the vue-google-maps documentation)
     */
    google: gmapApi
  },
  methods: {
    isEmpty: isEmpty,
    /**
     * Initialize the map.
     * This takes the vehicle state and pulls the lat/long and creates GPS markers and a gmaps Polyline.
     */
    initializeMap () {
      const bounds = new this.google.maps.LatLngBounds()
      const map = this.map
      const infowindow = new this.google.maps.InfoWindow()
      this.vehiclePath = new this.google.maps.Polyline({
        strokeColor: '#2c5699',
        strokeOpacity: 1,
        strokeWeight: 3,
        map: map
      })
      const path = this.vehiclePath.getPath()
      each(this.vehicleStates, state => {
        const lat = state.getField('lat')
        const long = state.getField('long')
        if (lat && long && state.timestamp) {
          const latlng = new this.google.maps.LatLng(lat, long)
          bounds.extend(latlng)
          path.push(latlng)
          const marker = new this.google.maps.Marker({
            position: latlng,
            icon: {
              url: '/img/map-marker-circle-red.png',
              scaledSize: new this.google.maps.Size(12, 12),
              anchor: new this.google.maps.Point(6, 6),
              zIndex: 50
            },
            info: `<h6>${Moment(state.timestamp).tz(this.customerContext.timezone()).format('lll zz')}</h6><span>${lat}, ${long}</span>`,
            map: map
          })
          this.gpsMarkers.push(marker)
          // Displays the InfoWindow when the marker is clicked
          this.google.maps.event.addListener(marker, 'click', (function (marker) {
            return function () {
              infowindow.setContent(marker.info)
              infowindow.open(map, marker)
            }
          })(marker))
        }
      })
      map.fitBounds(bounds)
      map.panToBounds(bounds)
    },
    async fetchVehicleStates () {
      this.loading = true
      try {
        const cutoff = Moment().subtract(24, 'hours').format()
        const resp = await this.$orm('VehicleData').findAll({
          vehicle_id: this.vehicle.id,
          timestamp_after: cutoff,
          sort: 'timestamp'
        })
        this.vehicleStates = resp.getItems()
        this.loaded = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    this.fetchVehicleStates().then(() => {
      // Initialize the Google Maps state once the map promise is resolved.
      this.$refs.map.$mapPromise.then((map) => {
        this.map = map
        this.initializeMap()
      })
    })
  }
}
</script>

<style scoped lang="scss">
.vehicle-gps-map {
  height: 400px;
}
</style>
